import { useRef, useState, useEffect } from "react"
import { PrimaryHeadingTextGalleryType } from "../../types/mainModulTypes"
import { Navigation } from "swiper/modules"
import { Swiper as SwiperType } from "swiper"
import { Swiper, SwiperSlide } from "swiper/react"
import "swiper/css"
import "swiper/css/navigation"
import { API } from "../../constants/constants"
import BaseHeadline from "../common/BaseHeadline"
import BaseRichtext from "../common/BaseRichtext"
import { HashLink } from "react-router-hash-link"
import { useAccessibility } from "../../context/AccessibilityContext"
import BaseSubheadline from "../common/BaseSubheadline"

export default function PrimaryHeadingTextGallery(props: PrimaryHeadingTextGalleryType) {
    const swiperRef = useRef<SwiperType | null>(null)
    const [isBeginning, setIsBeginning] = useState(true)
    const [isEnd, setIsEnd] = useState(false)

    useEffect(() => {
        if (swiperRef.current) {
            setIsBeginning(swiperRef.current.isBeginning)
            setIsEnd(swiperRef.current.isEnd)
        }
    }, [])

    const handlePrev = () => {
        if (swiperRef.current) {
            swiperRef.current.slidePrev()
            setTimeout(() => {
                setIsBeginning(swiperRef.current?.isBeginning ?? true)
                setIsEnd(swiperRef.current?.isEnd ?? false)
            }, 50) // Kleiner Delay, damit Swiper den Zustand updatet
        }
    }

    const handleNext = () => {
        if (swiperRef.current) {
            swiperRef.current.slideNext()
            setTimeout(() => {
                setIsBeginning(swiperRef.current?.isBeginning ?? true)
                setIsEnd(swiperRef.current?.isEnd ?? false)
            }, 50)
        }
    }

    const { getCursorPointerClass } = useAccessibility()

    const [shuffledSlides, setShuffledSlides] = useState(props.primaryHeadingTextGalleryGallery || [])

    useEffect(() => {
        // Funktion zum Mischen des Arrays
        const shuffleArray = (array: any[]) => {
            return [...array].sort(() => Math.random() - 0.5)
        }

        // Array neu mischen und setzen
        setShuffledSlides(shuffleArray(props.primaryHeadingTextGalleryGallery || []))
    }, [props.primaryHeadingTextGalleryGallery]) // Neu mischen, wenn sich die Props ändern

    return (
        <div className="max-w-content mx-auto flex flex-col items-center justify-center gap-8 px-4 lg:px-0">
            <div className="text-center">
                <BaseHeadline headlineType={2} label={props.primaryHeadingTextGalleryHeading} />
                <BaseRichtext classProps="max-w-3xl pt-4" content={props?.primaryHeadingTextGalleryText} />
            </div>
            <div className="max-w-content mx-auto flex gap-4 relative items-center">
                <button
                    onClick={handlePrev}
                    disabled={isBeginning}
                    className={`w-0 h-0 border-t-[50px] border-t-transparent border-b-[50px] border-b-transparent border-l-[30px] 
                                    ${
                                        isBeginning ? "border-l-gray-400 cursor-not-allowed" : `border-l-base-300 ${getCursorPointerClass()}`
                                    } rotate-180`}
                />
                <Swiper
                    spaceBetween={50}
                    slidesPerView={1}
                    breakpoints={{
                        640: { slidesPerView: 2 },
                        768: { slidesPerView: 3 },
                        1024: { slidesPerView: 4 },
                    }}
                    modules={[Navigation]}
                    onBeforeInit={(swiper: SwiperType) => {
                        swiperRef.current = swiper
                        setTimeout(() => {
                            setIsBeginning(swiper.isBeginning)
                            setIsEnd(swiper.isEnd)
                        }, 50)
                    }}
                    onSlideChange={(swiper: SwiperType) => {
                        setTimeout(() => {
                            setIsBeginning(swiper.isBeginning)
                            setIsEnd(swiper.isEnd)
                        }, 50)
                    }}
                    className="mySwiper max-w-[180px] sm:max-w-[500px] md:max-w-[600px] lg:max-w-4xl xl:max-w-page">
                    {shuffledSlides.map((slide, index) => (
                        <SwiperSlide key={index} className="">
                            <HashLink to={slide.primaryHeadingTextGalleryGalleryItemUrl} className={`group  aspect-square relative [perspective:1000px]  ${getCursorPointerClass()}`}>
                                <div className="relative w-full h-full transition-transform duration-700 md:[transform-style:preserve-3d] md:group-hover:[transform:rotateY(180deg)]">
                                    {/* Vorderseite */}
                                    <div
                                        className={`relative rounded-xl overflow-hidden border-4 border-${slide?.primaryHeadingTextGalleryGalleryItemColor}`}
                                        style={{ borderColor: slide.primaryHeadingTextGalleryGalleryItemColor }}>
                                        <img
                                            src={API + slide?.primaryHeadingTextGalleryGalleryItemImage?.url}
                                            alt={
                                                slide.primaryHeadingTextGalleryGalleryItemImage?.description ??
                                                slide.primaryHeadingTextGalleryGalleryItemImage?.name ??
                                                "AltTag"
                                            }
                                            className="w-full aspect-square object-cover"
                                        />
                                        <img
                                            src={API + slide?.primaryHeadingTextGalleryGalleryItemIcon?.url}
                                            alt={
                                                slide?.primaryHeadingTextGalleryGalleryItemIcon?.description ??
                                                slide.primaryHeadingTextGalleryGalleryItemIcon?.name ??
                                                "AltTag"
                                            }
                                            className={`absolute w-12 h-12 object-contain rounded-md bg-white border-2 top-0 left-0 p-1`}
                                            style={{ borderColor: slide.primaryHeadingTextGalleryGalleryItemColor }}
                                        />
                                    </div>
                                    {/* Hinterseite */}
                                    <div
                                        className="absolute inset-0 flex justify-center items-center [backface-visibility:hidden] [transform:rotateY(180deg)] border-8 rounded-default"
                                        style={{
                                            borderColor: slide.primaryHeadingTextGalleryGalleryItemColor,
                                            backgroundColor: slide.primaryHeadingTextGalleryGalleryItemColor,
                                        }}>
                                        <BaseSubheadline
                                            classProps={`text-center ${
                                                slide.primaryHeadingTextGalleryGalleryItemTextColor ? "!text-neutral" : "!text-accent"
                                            }`}
                                            label={slide.primaryHeadingTextGalleryGalleryItemText}
                                        />
                                    </div>
                                </div>
                                <BaseRichtext
                                    content={slide.primaryHeadingTextGalleryGalleryItemText}
                                    classProps="md:hidden text-center font-bold text-xl  pt-2"
                                />
                            </HashLink>
                        </SwiperSlide>
                    ))}
                </Swiper>
                <button
                    onClick={handleNext}
                    disabled={isEnd}
                    className={`w-0 h-0 border-t-[50px] border-t-transparent border-b-[50px] border-b-transparent border-l-[30px] 
                            ${isEnd ? "border-l-gray-400 cursor-not-allowed" : `border-l-base-300 ${getCursorPointerClass()}`}`}
                />
            </div>
        </div>
    )
}
